import { CustomizeAgentToolRenderer } from './customize-agent-tool-renderer';
import { ConfigureToolRenderer, LoadToolRenderer } from './load-tool-renderer';
import {
  AskForCredentialsMessageRenderer,
  AskForCredentialsOutput,
  AskForCredentialsRenderer,
  WaitingForCredentials,
} from './ask-for-credentials-renderer';
import { SearchToolRenderer } from './search-tool-renderer';
import { SiteScraperToolRenderer } from './site-scraper-tool-renderer';
import { WebSearchToolRenderer } from './web-search-tool-renderer';
import { UserOptionsRenderer } from './user-options-renderer';
import { SelectFolderRenderer } from './select-folder-renderer';
import { SleepToolRenderer } from './sleep-tool-renderer';
import { RelayToolRenderer } from './relay-tool-renderer';
import { coco } from '@cotera/sdk/core';
import { registerInputHijacker } from '../input-hijackers';
import { LoadToolOutput } from './types';
import { registerMessageFooter } from '../message-footers';
// `ToolRendererProps` and `useOwnerOrgId` live in a leaf module so that the
// renderers this barrel imports (e.g. ask-for-credentials) can reach
// `useOwnerOrgId` without importing back into this barrel — that value cycle is
// what produced the "Cannot access … before initialization" TDZ crash in prod.
import type { ToolRendererProps } from './tool-renderer-props';
export { useOwnerOrgId } from './tool-renderer-props';
export type { ToolRendererProps } from './tool-renderer-props';

export type CustomToolRenderer = React.FC<ToolRendererProps>;

// Registry mapping tool names to custom renderers
const CUSTOM_TOOL_RENDERERS: Record<string, CustomToolRenderer> = {};

export function registerToolRenderer(
  toolName: string,
  renderer: CustomToolRenderer
): void {
  CUSTOM_TOOL_RENDERERS[toolName] = renderer;
}

export function getCustomToolRenderer(
  toolName: string
): CustomToolRenderer | null {
  return CUSTOM_TOOL_RENDERERS[toolName] ?? null;
}

// Register built-in renderers
// Tool names from LLM have slashes converted to underscores
registerToolRenderer(
  coco.COCO_CREATE_AGENT_TOOL_KEY,
  CustomizeAgentToolRenderer
);
registerToolRenderer(coco.COCO_EDIT_AGENT_TOOL_KEY, CustomizeAgentToolRenderer);
registerToolRenderer(coco.COCO_LOAD_RESOURCE_TOOL_KEY, LoadToolRenderer);
registerToolRenderer('coco/load-tool', LoadToolRenderer);
registerToolRenderer(
  coco.COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  AskForCredentialsMessageRenderer
);
registerToolRenderer(coco.COCO_SEARCH_TOOL_KEY, SearchToolRenderer);
registerToolRenderer('coco/search', SearchToolRenderer);
registerToolRenderer(coco.COCO_SITE_SCRAPER_TOOL_KEY, SiteScraperToolRenderer);
registerToolRenderer(coco.COCO_WEB_SEARCH_TOOL_KEY, WebSearchToolRenderer);
registerToolRenderer(coco.COCO_USER_OPTIONS_TOOL_KEY, UserOptionsRenderer);
registerToolRenderer(coco.COCO_SELECT_FOLDER_TOOL_KEY, SelectFolderRenderer);
registerToolRenderer(coco.COCO_SLEEP_TOOL_KEY, SleepToolRenderer);
registerToolRenderer(coco.COCO_RELAY_TOOL_KEY, RelayToolRenderer);

registerInputHijacker(coco.COCO_SELECT_FOLDER_TOOL_KEY, SelectFolderRenderer, {
  predicate: (part) => part.isIncomplete(),
  inputPlaceholder: 'Or type a message instead…',
});

// User-options prompts hijack the input the same way an approval does, so the
// choices render in the input box (embedded, no card) instead of as a separate
// card in the message thread. The inline renderer above still draws the
// completed summary once a selection has been submitted.
registerInputHijacker(coco.COCO_USER_OPTIONS_TOOL_KEY, UserOptionsRenderer, {
  predicate: (part) => part.isIncomplete(),
  inputPlaceholder: 'Pick an option, or type a message instead…',
});

registerInputHijacker(coco.COCO_LOAD_RESOURCE_TOOL_KEY, ConfigureToolRenderer, {
  predicate: (part) => {
    if (part.isIncomplete()) {
      const output = part.getOutput<LoadToolOutput>();
      if (
        output !== undefined &&
        output.requiresCredentials === true &&
        output.credentialInfo !== undefined &&
        output.credentialInfo.strategies.length > 0 &&
        // Only show credential form if the tool failed to load
        output.success !== true
      ) {
        return true;
      }
    }
    return false;
  },
  inputPlaceholder: 'Or skip to configure later...',
});

registerInputHijacker(
  coco.COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  AskForCredentialsRenderer,
  {
    predicate: (part) => {
      return (
        part.isIncomplete() &&
        part.getOutput<AskForCredentialsOutput>()?.success !== false
      );
    },
    inputPlaceholder: 'Or skip to configure later...',
  }
);

registerInputHijacker('coco/load-tool', ConfigureToolRenderer, {
  predicate: (part) => {
    if (part.isIncomplete()) {
      const output = part.getOutput<LoadToolOutput>();
      if (
        output !== undefined &&
        output.requiresCredentials === true &&
        output.credentialInfo !== undefined &&
        output.credentialInfo.strategies.length > 0 &&
        output.success !== true
      ) {
        return true;
      }
    }
    return false;
  },
  inputPlaceholder: 'Or skip to configure later...',
});

registerMessageFooter({
  id: coco.COCO_LOAD_RESOURCE_TOOL_KEY,
  priority: 10,
  showLatestOnly: true,
  shouldShow: (messages) => {
    return messages.some(
      (message) =>
        message.isAssistantMessage() &&
        message.parts?.some(
          (part) =>
            part.isToolPart() &&
            part.isIncomplete() &&
            (part
              .toolKey?.()
              .defensiveEqual(coco.COCO_LOAD_RESOURCE_TOOL_KEY) ||
              part.toolKey?.().toString() === 'coco/load-tool') &&
            part.getOutput<LoadToolOutput>()?.requiresCredentials === true
        )
    );
  },
  render: () => <WaitingForCredentials />,
});

registerMessageFooter({
  id: coco.COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  priority: 10,
  showLatestOnly: true,
  shouldShow: (messages) => {
    return messages.some(
      (message) =>
        message.isAssistantMessage() &&
        message.parts?.some(
          (part) =>
            part.isToolPart() &&
            part.isIncomplete() &&
            part
              .toolKey?.()
              .defensiveEqual(coco.COCO_ASK_FOR_CREDENTIALS_TOOL_KEY) &&
            part.getOutput<AskForCredentialsOutput>()?.success !== false
        )
    );
  },
  render: () => <WaitingForCredentials />,
});
